// ===== FILE: src/ContentBlocks/renderers/TimelineRenderer.js =====
// A generic, horizontal "milestone / project timeline" block.
//
// It renders a segmented colour bar with numbered nodes; each step has an
// alternating callout (above / below the bar) holding a title, an icon, an
// optional in-bar time-span label, and a block of text. Everything is edited
// inline in edit mode. Sample / Import / Export JSON buttons let the data be
// round-tripped as a file (handy for handing the schema to an AI).
//
// The block is intentionally generic — "time span" is just a label, so the
// same block works for roadmaps, process steps, course weeks, etc.

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Download, Upload, FileText, Plus, Trash2 } from 'lucide-react';

export const TIMELINE_EXPORT_TYPE = 'boppps-timeline';

const DEFAULT_TIMELINE_COLORS = ['#10b981', '#2563eb', '#db2777', '#4f46e5', '#ef4444', '#f59e0b', '#0891b2', '#7c3aed'];

let timelineStepSeq = 0;
const makeStepId = () => `tl-${Date.now().toString(36)}-${(timelineStepSeq++).toString(36)}`;

export const createTimelineStep = (index = 0, overrides = {}) => ({
  id: makeStepId(),
  color: DEFAULT_TIMELINE_COLORS[index % DEFAULT_TIMELINE_COLORS.length],
  timeSpan: '',
  title: '',
  icon: '',
  text: '',
  // Even steps sit below the bar, odd steps above — a classic zig-zag.
  position: index % 2 === 0 ? 'below' : 'above',
  ...overrides
});

export const createTimelineBlockData = () => ({
  type: 'project-timeline',
  title: 'Project Timeline',
  steps: Array.from({ length: 5 }, (_, index) => createTimelineStep(index))
});

// Normalises any loose step object coming from an import into our shape.
export const sanitizeTimelineStep = (raw = {}, index = 0) => {
  const base = createTimelineStep(index);
  if (!raw || typeof raw !== 'object') return base;
  const position = raw.position === 'above' || raw.position === 'below' ? raw.position : base.position;
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : base.id,
    color: typeof raw.color === 'string' && raw.color.trim() ? raw.color.trim() : base.color,
    timeSpan: typeof raw.timeSpan === 'string' ? raw.timeSpan : (typeof raw.timespan === 'string' ? raw.timespan : ''),
    title: typeof raw.title === 'string' ? raw.title : '',
    icon: typeof raw.icon === 'string' ? raw.icon : '',
    text: typeof raw.text === 'string' ? raw.text : (typeof raw.description === 'string' ? raw.description : ''),
    position
  };
};

const getSteps = (block) => (Array.isArray(block?.steps) && block.steps.length > 0
  ? block.steps
  : createTimelineBlockData().steps);

const contrastText = (hex) => {
  if (typeof hex !== 'string') return '#ffffff';
  const clean = hex.replace('#', '');
  if (clean.length !== 3 && clean.length !== 6) return '#ffffff';
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return '#ffffff';
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#1f2937' : '#ffffff';
};

export const buildTimelineSamplePayload = () => ({
  type: TIMELINE_EXPORT_TYPE,
  version: 1,
  _readme: {
    description: 'Timeline / milestone block for LessonPlanner One. Import it into a Project Timeline block with the "Import JSON" button. Generic on purpose — use it for roadmaps, process steps, course weeks, project phases, etc.',
    howToUse: 'Only "title" and the "steps" array are read on import. Keys beginning with "_" (like this one) are ignored, so this sample is also directly importable.',
    fieldNotes: {
      title: 'Optional heading shown above the timeline.',
      'steps[].timeSpan': 'Short label shown inside the coloured bar segment (e.g. "Q1 2022", "July", "Week 1", "Phase 1"). Optional.',
      'steps[].title': 'Bold title shown in the step callout.',
      'steps[].icon': 'A single emoji or 1–2 characters shown in the round node badge (e.g. "🚀", "✅", "1"). Optional.',
      'steps[].text': 'Description paragraph shown in the callout box.',
      'steps[].color': 'Hex colour for the segment (e.g. "#2563eb"). Optional — a default palette is used if omitted.',
      'steps[].position': '"above" or "below" — which side of the bar the callout sits on. Optional; defaults to a zig-zag (even = below, odd = above).'
    }
  },
  title: 'Product Launch Roadmap',
  steps: [
    { color: '#10b981', timeSpan: 'Q1 2022', title: 'Discovery', icon: '🔍', text: 'Research the market, interview users, and define the problem.', position: 'below' },
    { color: '#2563eb', timeSpan: 'Q2 2022', title: 'Design', icon: '✏️', text: 'Prototype the solution and validate it with early testers.', position: 'above' },
    { color: '#db2777', timeSpan: 'Q3 2022', title: 'Build', icon: '🛠️', text: 'Develop the core features and set up the release pipeline.', position: 'below' },
    { color: '#4f46e5', timeSpan: 'Q4 2022', title: 'Beta', icon: '🧪', text: 'Ship to a limited audience and gather feedback.', position: 'above' },
    { color: '#ef4444', timeSpan: 'Q1 2023', title: 'Launch', icon: '🚀', text: 'Public release, marketing push, and ongoing support.', position: 'below' }
  ]
});

export const downloadTimelineJson = (payload, filename) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

// The timeline is laid out at a fixed design width (wide enough that each
// step column is readable) and then uniformly scaled — up OR down — so it
// always fills its container at identical proportions. That keeps it looking
// the same in the edit view, in full screen, and in the exported slide, and
// lets the canvas gizmo drive its size by changing the frame width.
const TIMELINE_COLUMN_WIDTH = 210;
const TIMELINE_MIN_WIDTH = 720;
const timelineBaseWidth = (stepCount) => Math.max(TIMELINE_MIN_WIDTH, Math.max(1, stepCount) * TIMELINE_COLUMN_WIDTH);

const TimelineRenderer = ({ block, isEditMode = false, onBlockUpdate }) => {
  const steps = getSteps(block);
  const title = typeof block?.title === 'string' ? block.title : '';
  const [status, setStatus] = useState('');
  const fileInputRef = useRef(null);

  const baseWidth = timelineBaseWidth(steps.length);
  const fitWrapRef = useRef(null);
  const fitInnerRef = useRef(null);
  const [fitScale, setFitScale] = useState(1);
  const [fitHeight, setFitHeight] = useState(null);

  useLayoutEffect(() => {
    const wrap = fitWrapRef.current;
    const inner = fitInnerRef.current;
    if (!wrap || !inner || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const width = wrap.clientWidth;
      if (!width) return;
      // Always fill the container width at the same proportions.
      const scale = Math.max(0.05, width / baseWidth);
      setFitScale((prev) => (Math.abs(prev - scale) > 0.004 ? scale : prev));
      const naturalHeight = inner.offsetHeight;
      setFitHeight(naturalHeight > 0 ? naturalHeight * scale : null);
    };

    const observer = new ResizeObserver(update);
    observer.observe(wrap);
    observer.observe(inner);
    update();
    return () => observer.disconnect();
  }, [baseWidth]);

  const flash = (message) => {
    setStatus(message);
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setStatus(''), 2600);
  };

  const commit = (updates) => onBlockUpdate?.({ ...block, ...updates });

  const updateStep = (index, patch) => {
    const next = steps.map((step, i) => (i === index ? { ...step, ...patch } : step));
    commit({ steps: next });
  };

  const addStep = () => {
    const next = [...steps, createTimelineStep(steps.length)];
    commit({ steps: next });
  };

  const removeStep = (index) => {
    if (steps.length <= 1) return;
    commit({ steps: steps.filter((_, i) => i !== index) });
  };

  // ----- Import / Export / Sample -----
  const handleExport = () => {
    const payload = {
      type: TIMELINE_EXPORT_TYPE,
      version: 1,
      exportedAt: new Date().toISOString(),
      title,
      steps: steps.map(({ color, timeSpan, title: stepTitle, icon, text, position }) => ({
        color, timeSpan, title: stepTitle, icon, text, position
      }))
    };
    const slug = (title || 'timeline').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'timeline';
    downloadTimelineJson(payload, `${slug}.timeline.json`);
    flash('Exported timeline to JSON.');
  };

  const handleSample = () => {
    downloadTimelineJson(buildTimelineSamplePayload(), 'timeline-sample.json');
    flash('Downloaded sample timeline JSON.');
  };

  const applyImported = (data) => {
    let rawSteps = [];
    let nextTitle = title;
    if (Array.isArray(data)) {
      rawSteps = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.steps)) rawSteps = data.steps;
      if (typeof data.title === 'string') nextTitle = data.title;
    }
    const cleaned = rawSteps
      .filter((entry) => entry && typeof entry === 'object')
      .map((entry, index) => sanitizeTimelineStep(entry, index));
    if (!cleaned.length) {
      flash('No timeline steps found in that file.');
      return;
    }
    commit({ title: nextTitle, steps: cleaned });
    flash(`Imported ${cleaned.length} step${cleaned.length === 1 ? '' : 's'}.`);
  };

  const handleImportFile = (event) => {
    const file = event.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          applyImported(JSON.parse(ev.target?.result || '{}'));
        } catch (err) {
          console.error('Timeline import failed', err);
          flash('Could not read that file as JSON.');
        }
      };
      reader.readAsText(file);
    }
    if (event.target) event.target.value = '';
  };

  const toolbarBtn = 'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100';

  const renderCallout = (step, index, side) => {
    if (step.position !== side) {
      // Reserve the column height on the empty side so the bar stays aligned.
      return <div className="w-full" aria-hidden="true" />;
    }
    const hasIcon = Boolean((step.icon || '').trim());
    return (
      <div className={`flex w-full flex-col items-center gap-2 px-2 ${side === 'above' ? 'justify-end' : 'justify-start'}`}>
        {isEditMode ? (
          <input
            type="text"
            value={step.title}
            onChange={(e) => updateStep(index, { title: e.target.value })}
            placeholder="Title"
            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-center text-sm font-bold italic text-slate-700"
          />
        ) : (
          step.title ? <h4 className="text-center text-lg font-bold italic text-slate-700">{step.title}</h4> : null
        )}

        <div
          className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-2 border-dashed text-sm italic"
          style={{ borderColor: step.color, color: step.color }}
        >
          {isEditMode ? (
            <input
              type="text"
              value={step.icon}
              onChange={(e) => updateStep(index, { icon: e.target.value })}
              placeholder="Icon"
              maxLength={3}
              className="h-12 w-12 rounded-full border-0 bg-transparent text-center text-2xl focus:outline-none"
              title="Emoji or 1–2 characters"
            />
          ) : (
            <span className="text-2xl not-italic">{hasIcon ? step.icon : ''}</span>
          )}
        </div>

        {isEditMode ? (
          <textarea
            value={step.text}
            onChange={(e) => updateStep(index, { text: e.target.value })}
            placeholder="Type text here"
            rows={3}
            className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
          />
        ) : (
          step.text ? (
            <div className="w-full rounded-md bg-slate-100 px-3 py-2 text-sm leading-snug text-left text-slate-700 whitespace-pre-line">{step.text}</div>
          ) : null
        )}

        {isEditMode && (
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-500" title="Segment colour">
              <input
                type="color"
                value={/^#([0-9a-f]{6})$/i.test(step.color) ? step.color : '#2563eb'}
                onChange={(e) => updateStep(index, { color: e.target.value })}
                className="h-6 w-6 cursor-pointer rounded border border-slate-300 bg-white p-0"
              />
            </label>
            <button
              type="button"
              onClick={() => updateStep(index, { position: side === 'above' ? 'below' : 'above' })}
              className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-500 hover:bg-slate-100"
              title="Flip to the other side of the bar"
            >
              Flip
            </button>
            {steps.length > 1 && (
              <button
                type="button"
                onClick={() => removeStep(index)}
                className="inline-flex items-center gap-1 rounded border border-rose-200 px-2 py-0.5 text-[11px] font-semibold text-rose-600 hover:bg-rose-50"
                title="Remove this step"
              >
                <Trash2 size={11} /> Remove
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const columnBasis = `${100 / steps.length}%`;

  return (
    <div className="my-6">
      {isEditMode && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button type="button" onClick={handleSample} className={toolbarBtn} title="Download a documented sample JSON — hand it to an AI so it knows the timeline format">
            <FileText size={12} /> Sample JSON
          </button>
          <button type="button" onClick={() => fileInputRef.current?.click()} className={toolbarBtn} title="Import a timeline from a JSON file">
            <Upload size={12} /> Import JSON
          </button>
          <button type="button" onClick={handleExport} className={toolbarBtn} title="Export this timeline to a JSON file">
            <Download size={12} /> Export JSON
          </button>
          <button type="button" onClick={addStep} className={`${toolbarBtn} text-slate-700`} title="Add a step">
            <Plus size={12} /> Add step
          </button>
          {status && <span className="text-xs font-medium text-emerald-600">{status}</span>}
          <input ref={fileInputRef} type="file" accept="application/json,.json" onChange={handleImportFile} className="hidden" />
        </div>
      )}

      {isEditMode ? (
        <input
          type="text"
          value={title}
          onChange={(e) => commit({ title: e.target.value })}
          placeholder="Timeline title (optional)"
          className="mb-4 w-full max-w-md rounded-md border border-slate-300 px-3 py-2 text-lg font-semibold text-slate-800"
        />
      ) : (
        title ? <h3 className="mb-4 text-2xl font-semibold text-slate-800">{title}</h3> : null
      )}

      <div
        ref={fitWrapRef}
        className="w-full overflow-hidden"
        style={fitHeight !== null ? { height: `${fitHeight}px` } : undefined}
      >
        <div
          ref={fitInnerRef}
          style={{
            width: `${baseWidth}px`,
            transform: `scale(${fitScale})`,
            transformOrigin: 'top left'
          }}
        >
          {/* Above-bar callouts */}
          <div className="flex items-end" style={{ minHeight: '210px' }}>
            {steps.map((step, index) => (
              <div key={`above-${step.id}`} style={{ flex: `0 0 ${columnBasis}`, maxWidth: columnBasis }}>
                {renderCallout(step, index, 'above')}
              </div>
            ))}
          </div>

          {/* The bar */}
          <div className="relative flex py-1" style={{ minHeight: '52px' }}>
            {steps.map((step, index) => {
              const textColor = contrastText(step.color);
              return (
                <div
                  key={`bar-${step.id}`}
                  className={`relative flex min-h-[44px] items-center justify-center ${index === 0 ? 'rounded-l-full' : ''} ${index === steps.length - 1 ? 'rounded-r-full' : ''}`}
                  style={{ flex: `0 0 ${columnBasis}`, maxWidth: columnBasis, backgroundColor: step.color }}
                >
                  {/* Node badge at the segment's left edge */}
                  <div
                    className="absolute left-0 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-sm font-bold shadow"
                    style={{ color: '#334155', zIndex: 2 }}
                  >
                    {index + 1}
                  </div>

                  <div className="px-6 text-center leading-tight" style={{ color: textColor }}>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={step.timeSpan}
                        onChange={(e) => updateStep(index, { timeSpan: e.target.value })}
                        placeholder="Time span"
                        className="w-full rounded border-0 bg-white/25 px-2 py-0.5 text-center text-xs font-semibold placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/60"
                        style={{ color: textColor }}
                      />
                    ) : (
                      step.timeSpan ? <span className="text-xs font-semibold">{step.timeSpan}</span> : null
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Below-bar callouts */}
          <div className="flex items-start" style={{ minHeight: '210px' }}>
            {steps.map((step, index) => (
              <div key={`below-${step.id}`} style={{ flex: `0 0 ${columnBasis}`, maxWidth: columnBasis }}>
                {renderCallout(step, index, 'below')}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineRenderer;
